/**
 * PayMix adjustment range — how far the candidate may shift base salary
 * (cash ↔ equity) around the offered base salary when PayMix adjustment is
 * enabled.
 *
 * The percentage is configurable per offer (stored on `offers.paymix_variation_pct`).
 * When an offer has no explicit value it falls back to the default below.
 *
 * These helpers are the single source of truth for both the client slider
 * (paymix-calculator) and the server-side acceptance validation (public-offer),
 * so the two can never drift. Never hardcode the number elsewhere.
 */

/** Default when an offer does not specify its own PayMix variation. */
export const PAYMIX_DEFAULT_VARIATION_PCT = 10;

/** Hard cap on the per-offer override, as a sanity guard against typos. */
export const PAYMIX_MAX_VARIATION_PCT = 20;

/** Clamp an arbitrary/null pct into [0, MAX], defaulting null/invalid to the default. */
export function resolvePaymixVariationPct(pct?: number | null): number {
  if (pct == null || !Number.isFinite(pct)) return PAYMIX_DEFAULT_VARIATION_PCT;
  return Math.min(Math.max(pct, 0), PAYMIX_MAX_VARIATION_PCT);
}

/** Fraction form of the resolved pct, e.g. 10 → 0.1. */
export function paymixVariationFraction(pct?: number | null): number {
  return resolvePaymixVariationPct(pct) / 100;
}

/** Human-readable "±10%" label for the resolved pct. */
export function paymixVariationLabel(pct?: number | null): string {
  return `±${resolvePaymixVariationPct(pct)}%`;
}
